import { useState, useEffect, useCallback, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Shield, Star, Wrench, Landmark, X, FlaskConical } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { isLabsMode, setLabsMode, onLabsModeChange } from '../lib/labs'

// ── AdminFab — the floating admin toolkit (Jason's spec, 2026-08-15) ─────────
// A site-wide floating button, rendered ONLY when the signed-in user's profile
// says is_admin (checked via the get_my_profile definer RPC — the same gate
// Aftershow's takedown path uses; the client flag is cosmetic, every write it
// unlocks is separately enforced by is_admin() RLS at the table).
//
// Tap → a compact sheet: the ADMIN MODE toggle plus jump links to the admin
// surfaces. Admin mode is a global broadcast (window.__nextepAdminMode +
// 'nextep:admin-mode' event) that content surfaces listen for — in v1 the
// Aftershow room responds by outlining each post and exposing Edit / Delete /
// ★ Feature-on-site actions (see Aftershow.jsx). The mode does NOT persist
// across reloads on purpose: an editing stance you have to re-arm can't be
// left on by accident.
//
// Placement: bottom-LEFT, above the nav. Every other floating actor in the
// app (AftershowIndex's "Open a New Room", toasts, celebration rail) lives
// bottom-center or bottom-right, so the left corner is the one spot that
// can't overlap an action button.

export function adminModeOn() {
  return typeof window !== 'undefined' && !!window.__nextepAdminMode
}

// Reactive version of adminModeOn() for any surface that needs to re-render the
// instant admin mode is flipped from the FAB — e.g. Face-Off/Cliffhanger's
// admin-only practice-replay (Jason 2026-08-22: "controlled by the FAB
// please"). useSyncExternalStore since the source of truth is a plain window
// global + a DOM event, not React state.
export function useAdminModeOn() {
  const subscribe = useCallback((cb) => {
    window.addEventListener('nextep:admin-mode', cb)
    return () => window.removeEventListener('nextep:admin-mode', cb)
  }, [])
  return useSyncExternalStore(subscribe, adminModeOn, () => false)
}

export function AdminFab({ user }) {
  const navigate = useNavigate()
  const [isAdmin, setIsAdmin] = useState(false)
  const [open, setOpen] = useState(false)
  // Seed from the actual global flag, not a bare `false` — if this component
  // ever remounts mid-session (Jason 2026-08-22: "toggling admin mode appears
  // to only have worked once, I can no longer use it") a fresh `useState(false)`
  // would show the switch OFF while window.__nextepAdminMode is still on, and
  // the FIRST tap after that would compute next = !false = true — i.e. re-enable
  // it and dispatch a no-op "on" event instead of the OFF the user was tapping
  // for. Reading the real flag here means the switch always reflects (and can
  // always correctly flip) the state every other surface is already using.
  const [mode, setMode] = useState(adminModeOn)

  useEffect(() => {
    if (!user?.id) { setIsAdmin(false); return }
    let alive = true
    supabase.rpc('get_my_profile').maybeSingle()
      .then(({ data }) => { if (alive) setIsAdmin(!!data?.is_admin) })
      .catch(() => {})
    return () => { alive = false }
  }, [user?.id])

  const toggleMode = () => {
    const next = !mode
    setMode(next)
    window.__nextepAdminMode = next
    window.dispatchEvent(new CustomEvent('nextep:admin-mode', { detail: { on: next } }))
  }

  // Sandbox: use the app as normal, write nothing (lib/labs.js). Persists across
  // reloads (a test session spans them), so the FAB wears a badge the whole time
  // it's on — the one thing that must never happen is forgetting it's on and
  // wondering why a real mark vanished on reload. The same flag drives the Labs
  // toggle in Settings; either switch moves both.
  const [sandbox, setSandbox] = useState(isLabsMode)
  useEffect(() => onLabsModeChange(setSandbox), [])
  const toggleSandbox = () => { const next = !sandbox; setLabsMode(next); setSandbox(next) }

  if (!isAdmin || !user) return null

  return createPortal((
    <>
      {sandbox && (
        <div aria-hidden="true" style={{
          position: 'fixed', left: 12, bottom: 'calc(var(--nav-h) + 58px)', zIndex: 'var(--z-menu)',
          pointerEvents: 'none', fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--amber-ink)', background: 'var(--amber)', borderRadius: 'var(--radius-pill)', padding: '3px 8px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.45)', whiteSpace: 'nowrap',
        }}>Sandbox · nothing saves</div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={sandbox ? 'Admin tools (sandbox on: nothing is being saved)' : 'Admin tools'}
        style={{
          // A half-hidden edge tab (2026-11-01): even at 40px/0.62 the disc sat on top
          // of "YOUR RUN" and the Verdict case footer on a phone. 12px now protrudes
          // A 24px tab on the screen edge sits inside the content gutter — reachable, never over a word.
          position: 'fixed', left: 0, bottom: 'calc(var(--nav-h) + 14px)',
          zIndex: 'var(--z-menu)', width: 24, height: 40, opacity: 0.55, padding: 0,
          borderRadius: '0 var(--radius-pill) var(--radius-pill) 0', cursor: 'pointer',
          background: mode
            ? 'linear-gradient(135deg, #6c5ce7, var(--purple))'
            : 'rgba(20,20,32,0.85)',
          border: sandbox ? '2px dashed var(--amber)' : `1.5px solid ${mode ? 'var(--purple)' : 'rgba(162,155,254,0.4)'}`,
          color: mode ? '#fff' : (sandbox ? 'var(--amber)' : 'var(--purple)'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: sandbox ? '0 0 18px rgba(var(--amber-rgb),0.45)' : (mode ? '0 0 18px rgba(108,92,231,0.5)' : '0 4px 14px rgba(0,0,0,0.45)'),
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
          transition: 'background 0.2s, box-shadow 0.2s, border-color 0.2s',
        }}
      >
        {sandbox ? <FlaskConical size={15} strokeWidth={2.2} /> : <Shield size={15} strokeWidth={2.2} />}
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-menu)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', left: 12, bottom: 'calc(var(--nav-h) + 62px)',
              width: 230, background: 'var(--sheet)', border: '1px solid rgba(162,155,254,0.3)',
              borderRadius: 'var(--radius-lg)', padding: 10,
              boxShadow: '0 16px 44px rgba(0,0,0,0.6)',
              '--nxa': 'adminFabIn 0.22s var(--ease-spring) both',
              fontFamily: 'var(--font)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 6px 8px' }}>
              <Shield size={13} color="var(--purple)" />
              <span style={{ color: 'var(--purple)', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', flex: 1 }}>Admin</span>
              <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 2, lineHeight: 1 }}><X size={13} /></button>
            </div>

            <button onClick={toggleMode} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 9,
              background: mode ? 'rgba(108,92,231,0.18)' : 'rgba(var(--ink-rgb),0.04)',
              border: `1px solid ${mode ? 'var(--purple)' : 'rgba(var(--ink-rgb),0.09)'}`,
              borderRadius: 'var(--radius)', padding: '10px 11px', cursor: 'pointer',
              fontFamily: 'var(--font)', textAlign: 'left', marginBottom: 6,
            }}>
              <Star size={15} color={mode ? 'var(--purple)' : 'var(--text-muted)'} fill={mode ? 'var(--purple)' : 'none'} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', color: 'var(--text-strong)', fontSize: 12.5, fontWeight: 700 }}>Admin mode</span>
                <span style={{ display: 'block', color: 'var(--text-dim)', fontSize: 10.5, marginTop: 1 }}>Edit, delete &amp; feature content in place</span>
              </span>
              <span style={{
                width: 30, height: 18, borderRadius: 'var(--radius-pill)', flexShrink: 0,
                background: mode ? 'var(--purple)' : 'rgba(var(--ink-rgb),0.14)', position: 'relative',
                transition: 'background 0.18s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: mode ? 14 : 2, width: 14, height: 14,
                  borderRadius: 'var(--radius-full)', background: '#fff', transition: 'left 0.18s var(--ease-spring)',
                }} />
              </span>
            </button>

            <button onClick={toggleSandbox} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 9,
              background: sandbox ? 'rgba(var(--amber-rgb),0.14)' : 'rgba(var(--ink-rgb),0.04)',
              border: `1px solid ${sandbox ? 'var(--amber)' : 'rgba(var(--ink-rgb),0.09)'}`,
              borderRadius: 'var(--radius)', padding: '10px 11px', cursor: 'pointer',
              fontFamily: 'var(--font)', textAlign: 'left', marginBottom: 6,
            }}>
              <FlaskConical size={15} color={sandbox ? 'var(--amber)' : 'var(--text-muted)'} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', color: 'var(--text-strong)', fontSize: 12.5, fontWeight: 700 }}>Sandbox</span>
                <span style={{ display: 'block', color: 'var(--text-dim)', fontSize: 10.5, marginTop: 1 }}>{sandbox ? 'On — nothing you do is saved. Reload to reset.' : 'Use the app as normal, write nothing'}</span>
              </span>
              <span style={{
                width: 30, height: 18, borderRadius: 'var(--radius-pill)', flexShrink: 0,
                background: sandbox ? 'var(--amber)' : 'rgba(var(--ink-rgb),0.14)', position: 'relative',
                transition: 'background 0.18s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: sandbox ? 14 : 2, width: 14, height: 14,
                  borderRadius: 'var(--radius-full)', background: '#fff', transition: 'left 0.18s var(--ease-spring)',
                }} />
              </span>
            </button>

            {[
              { icon: Wrench, label: 'War Room', to: '/admin' },
              { icon: Landmark, label: 'Canons', to: '/admin/canons' },
            ].map(({ icon: Icon, label, to }) => (
              <button key={to} onClick={() => { setOpen(false); navigate(to) }} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                background: 'none', border: 'none', borderRadius: 'var(--radius-sm)',
                padding: '8px 11px', cursor: 'pointer', fontFamily: 'var(--font)',
                color: 'var(--text-muted)', fontSize: 12.5, fontWeight: 600, textAlign: 'left',
              }}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
        </div>
      )}
      <style>{`@keyframes adminFabIn { from { opacity:0; transform: translateY(8px) scale(0.96); } to { opacity:1; transform: translateY(0) scale(1); } }`}</style>
    </>
  ), document.body)
}
